import { Component, OnInit, Input, EventEmitter, Output, ElementRef, ViewChild, AfterViewInit, OnChanges, signal, computed } from '@angular/core';
import { FlowObjectDefinition, FlowObjectInstance, FlowObjectInstanceState, FlowObjectType } from '../../../models/flow-object.model';
import { ToastrService } from 'ngx-toastr';
import { Enums } from '../../../shared/enums';
import { AuthService } from '../../../services/auth.service';
import { EDocsService } from '../../../services/edocs.service';
import { MatDialog } from '@angular/material/dialog';
import { PdfPreviewDialogComponent } from '../../pdf-preview-dialog/pdf-preview-dialog.component';
import { NgxSpinnerService } from 'ngx-spinner';
import { DocumentLegalValueCombinationDescription, Papel } from '../../../models/edocs.model';
import { Utils } from '../../../shared/utils';
import { FlowDefinition, FlowInstance, FlowInstanceState } from '../../../models/flow.model';
import { CookieService } from 'ngx-cookie-service';
import { InputDataTaskApprove, InputDataTaskForward, OutputDataTaskApprove } from '../../../models/input-output-data.model';
import { OrganogramaService } from '../../../services/organograma.service';
import { IMPORTS } from './_imports';
import { ConfigSchema, ExtraContentAttachment } from '../../../models/config-schema.model';
import { PdfUploadValue } from '../../../models/helper-classes.model';

@Component({
    selector: 'fob-approve',
    templateUrl: './fob-approve.component.html',
    styleUrl: './fob-approve.component.scss',
    standalone: true,
    imports: IMPORTS
})
export class FlowObjectApproveComponent implements OnInit, OnChanges, AfterViewInit {
    // #region [ViewChild]
    @ViewChild('papelRef') papelRef: ElementRef;
    // #endregion

    // #region [Type props]
    DocumentLegalValueCombinationDescription = DocumentLegalValueCombinationDescription;
    FlowObjectInstanceState = FlowObjectInstanceState;
    FlowObjectType = FlowObjectType;
    Utils = Utils;
    Enums = Enums;
    // #endregion

    // #region [props]
    outputData: OutputDataTaskApprove;
    // #endregion

    // #region [signals]
    $model = signal(null as FlowObjectInstance);
    $flowInstance = signal(null as FlowInstance);
    $inputData = signal(null as InputDataTaskApprove);
    $flowObjectDefinition = signal(null as FlowObjectDefinition);
    $configSchema = signal(null as ConfigSchema);
    $papeisSelector = signal([] as Papel[]);
    $recipient = signal('');
    $forwardingProtocol = signal(Enums.Messages.DefaultEmptyValue as string);
    $processProtocol = signal(Enums.Messages.DefaultEmptyValue as string);
    $previousDocuments = signal([] as any[]);
    $currentDocuments = signal([] as any[]);
    $attachmentDocuments = signal([] as any[]);
    $publicMessageHtml = signal(null as string);
    $isMarkedForApproval = signal(false);
    // #endregion

    // #region [computeds]
    $currentUser = computed(() => this.authService.$user());
    $isPublicMessageHtmlLoading = computed(() => Utils.isNullOrEmpty(this.$publicMessageHtml()));
    $hasPendingTask = computed(() => this.$model()?.stateId == FlowObjectInstanceState.Started);
    $isReadOnly = computed(() => [FlowObjectInstanceState.Finished, FlowObjectInstanceState.NotStarted].includes(this.$model()?.stateId) || this.$flowInstance()?.stateId === FlowInstanceState.CancelledByDeliberation);
    $isMissingMessage = computed(() => Utils.isNullOrEmpty(this.$inputData()?.message) || this.$inputData().message.trim().length < 5);
    $dateString = computed(() => this.$model() == null ? '' : new Date(this.$model().updateDate).toLocaleString());
    $hasAttachments = computed(() => this.$configSchema().taskApprove.extraContent?.attachments?.length > 0);
    $isPlural = computed(() => this.$configSchema()?.taskApprove?.extraContent?.attachments?.length > 1);
    $shouldLetPublicAgentInteract = computed(() =>
        this.$currentUser()?.isPublicAgent
        && this.$model()?.flowObjectInstanceActors.some(x => x.actorId == this.$currentUser()?.id)
        && (
            this.$flowInstance()?.ownerId != this.$currentUser()?.id
            || this.$currentUser()?.papeis?.some(x =>
                x.isCurrentUserHeadOfThisRolesOrganization
                && x.lotacaoGuid.trim().toLowerCase() == this.$flowInstance()?.currentActionableLocationId.trim().toLowerCase()
            )
        )
    );
    // #endregion

    // #region [Input/Output]
    @Input() _model: FlowObjectInstance;
    @Input() _flowInstance: FlowInstance;
    @Input() _flowDefinition: FlowDefinition;
    @Output() _submitEvent = new EventEmitter<FlowObjectInstance>();
    // #endregion

    constructor(
        private dialog: MatDialog,
        private spinner: NgxSpinnerService,
        private toastr: ToastrService,
        private cookieService: CookieService,
        private authService: AuthService,
        private eDocsService: EDocsService,
        private organogramaService: OrganogramaService
    ) { }

    async ngOnInit() {
        Utils.setSignal(this.$flowObjectDefinition, this._flowDefinition.flowObjectDefinitions.find(x => x.id == this.$model().flowObjectDefinitionId));
        Utils.setSignal(this.$configSchema, JSON.parse(this.$flowObjectDefinition().configSchema) as ConfigSchema);
        Utils.setSignal(this.$inputData, Object.assign(new InputDataTaskApprove(), JSON.parse(this.$model().inputData)));
        this.outputData = this.$model().outputData == null ? null : Object.assign(new OutputDataTaskApprove(), JSON.parse(this.$model().outputData));

        // #region [protocolo de Encaminhamento]
        const response_Encaminhamento = await this.eDocsService.getEncaminhamento(this.$inputData().eDocsData.forwardingId);

        if (!response_Encaminhamento.isSuccess) {
            this.toastr.error(response_Encaminhamento.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
            return;
        }

        const encaminhamento = response_Encaminhamento.data;
        this.$forwardingProtocol.set(encaminhamento.protocolo);
        // #endregion

        // #region [protocolo de Processo]
        if (Utils.isNotNullOrEmpty(this.$inputData().eDocsData.processId)) {
            const response_Processo = await this.eDocsService.getProcesso(this.$inputData().eDocsData.processId);

            if (!response_Processo.isSuccess) {
                this.toastr.error(response_Processo.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                return;
            }

            this.$processProtocol.set(response_Processo.data.protocolo);
        }
        // #endregion

        // #region [documentos capturados anteriormente à Tarefa corrente]
        for (let item of encaminhamento.documentosEncaminhamento.filter(x => !x.controle)) {
            if (Utils.isNotNullOrEmpty(this.outputData) && this.outputData.eDocsData.forwardingId == item.documento.encaminhamentoId) {
                break;
            }

            if (!this.$previousDocuments().some(x => x.registro == item.documento.registro)) {
                Utils.updSignal(this.$previousDocuments, x => {
                    if(!item.documento.nome.endsWith('Cancelamento Manual')) {
                        x.push({
                            id: item.documento.id,
                            registro: item.documento.registro,
                            filename: `${item.documento.nome}.${item.documento.extensao}`
                        });
                    }
                    return x;
                });
            }
        }
        // #endregion

        // #region [documentos capturados na Tarefa corrente]
        if (Utils.isNotNullOrEmpty(this.outputData)) {
            let filtered = encaminhamento.documentosEncaminhamento.filter(x =>
                x.documento.encaminhamentoId == this.outputData.eDocsData.forwardingId
                && !x.controle
            );

            for (let item of filtered) {
                if (!this.$previousDocuments().some(x => x.registro == item.documento.registro)) {
                    if(!item.documento.nome.endsWith('Cancelamento Manual')) {
                        Utils.updSignal(this.$currentDocuments, x => {
                            x.push({
                                id: item.documento.id,
                                registro: item.documento.registro,
                                filename: `${item.documento.nome}.${item.documento.extensao}`
                            });
                            return x;
                        });
                    }

                    let labels = this.$configSchema().taskApprove.extraContent?.attachments?.map(x => x.label) || [];
                    if (labels.some(x => item.documento.nome.split(' | ').at(-1).startsWith(x))) {
                        Utils.updSignal(this.$attachmentDocuments, x => {
                            x.push({
                                id: item.documento.id,
                                registro: item.documento.registro,
                                filename: `${item.documento.nome}.${item.documento.extensao}`
                            });
                            return x;
                        });
                    }
                }
            }
        }
        // #endregion

        // #region [determinação do destinatário]
        const response_LocationInfo = await this.organogramaService.getLocationInfo(this.$inputData().eDocsData.currentForwardingLocationId);

        if (!response_LocationInfo.isSuccess) {
            this.toastr.error(response_LocationInfo.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
            return;
        }

        const encaminhamentoDestino = response_LocationInfo.data;
        if (!encaminhamentoDestino.nome.includes(Enums.Messages.DefaultEmptyValue)) {
            this.$recipient.set(encaminhamentoDestino.nome
                + (!Utils.isStringEqual(encaminhamentoDestino.nome, encaminhamentoDestino.sigla) ? ` - ${encaminhamentoDestino.sigla}` : '')
                + ` - ${encaminhamentoDestino.paiSigla}`
                + (encaminhamentoDestino.paiSigla.endsWith(encaminhamentoDestino.patriarcaSigla) ? '' : ` - ${encaminhamentoDestino.patriarcaSigla}`)
            );
        } else {
            let lastForwardTask = this.$flowInstance().flowObjectInstances.filter(x =>
                x.flowObjectDefinition.typeId == FlowObjectType.TaskForward
                && x.stateId == FlowObjectInstanceState.Finished
            ).sort((a,b) => b.flowObjectDefinition.outerIndex - a.flowObjectDefinition.outerIndex)[0];

            if (lastForwardTask != null) {
                let inputData = JSON.parse(lastForwardTask.inputData) as InputDataTaskForward;
                this.$recipient.set(inputData.recipientName);
            }
        }
        // #endregion

        // #region [seletor de papéis]
        const response_UsuarioPapeisEncaminhamento = await this.eDocsService.getUsuarioPapeisEncaminhamento(this.$inputData().eDocsData.forwardingId);

        if (!response_UsuarioPapeisEncaminhamento.isSuccess) {
            this.toastr.error(response_UsuarioPapeisEncaminhamento.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
            return;
        }

        this.$papeisSelector.set(response_UsuarioPapeisEncaminhamento.data);

        // força o usuário a selecionar um papel manualmente
        setTimeout(() => Utils.updSignal(this.$inputData, x => {
            x.eDocsData.signerId = null;
            return x;
        }), 50);

        if (this.$papeisSelector().length == 0) {
            Utils.updSignal(this.$inputData, x => {
                x.eDocsData.signerId = this.$currentUser().id;
                return x;
            });
        }
        // #endregion

        // #region [mensagem pública customizada]
        if (Utils.isNotNullOrEmpty(this.$flowObjectDefinition()?.publicMessageHtml)) {
            setTimeout(() => this.$publicMessageHtml.set(Utils.getParsedMessageHtml(
                this.cookieService,
                this.$flowObjectDefinition().publicMessageHtml,
                this.$forwardingProtocol(),
                this.$processProtocol()
            )), 1000);
        }
        // #endregion
    }

    ngOnChanges() {
        this.$model.set(this._model);
        this.$flowInstance.set(this._flowInstance);
    }

    ngAfterViewInit() {
        if (this.$hasPendingTask() && this.$inputData().eDocsData.signerId == null) {
            Utils.updSignal(this.$inputData, x => {
                x.eDocsData.signerId = this.$currentUser().papeis.find(x => x.prioritario).guid;
                return x;
            });
        }
    }

    async openDocumentModal(id: string) {
        this.spinner.show('flowInstance');

        const response = await this.eDocsService.getDocumentoDownloadUrl(id);

        this.spinner.hide('flowInstance');

        if (!response.isSuccess) {
            this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
            return;
        }

        setTimeout(() => {
            this.dialog.open(PdfPreviewDialogComponent, {
                data: {
                    content: response.data
                }
            });
        }, 200);
    }

    signerIdChange(event) {
        Utils.updSignal(this.$inputData, x => {
            x.eDocsData.signerId = event;
            return x;
        });
    }

    onChangeMessage(event) {
        Utils.updSignal(this.$inputData, x => {
            x.message = event;
            return x;
        });
    }

    onPdfUploadChange(event: PdfUploadValue, attachment: ExtraContentAttachment) {
        let buffer = (this.$inputData().attachments || []).filter(x => x.id != attachment.id);
        buffer.push({
            id: attachment.id,
            identificadorTemporarioArquivoNaNuvem: event.minioKey[0]
        });
        Utils.updSignal(this.$inputData, x => {
            x.attachments = buffer;
            return x;
        });
    }

    getDocumentLegalValueCombination(item: ExtraContentAttachment): string {
        return this.DocumentLegalValueCombinationDescription.get(+item.documentLegalValueCombination).split(':')[1].trim();
    }

    getLegalReasoningsNames(item: ExtraContentAttachment): string {
        return item.accessLevel.legalReasoningsNames.map(x => x.split(' - ')[1].trim().toLocaleUpperCase()).join('; ');
    }

    toggleMarkForApproval() {
        if (!this.validateInput()) return;

        this.$isMarkedForApproval.set(!this.$isMarkedForApproval());
    }

    submitApproval(hasApproved: boolean) {
        if (!this.validateInput()) return;

        Utils.updSignal(this.$inputData, x => {
            x.hasApproved = hasApproved;
            return x;
        });
        Utils.updSignal(this.$model, x => {
            x.inputData = JSON.stringify(this.$inputData());
            return x;
        });

        this._submitEvent.emit(this.$model());
    }

    // #region [===== private methods =====]
    // #endregion

    private validateInput(): boolean {
        if (this.$isMissingMessage()) {
            this.toastr.warning(Enums.Messages.MessageMinlengthError, Enums.Messages.Warning, Utils.getToastrErrorOptions());
            return false;
        }

        if (Utils.isNullOrEmpty(this.$inputData().eDocsData.signerId)) {
            this.toastr.warning(Enums.Messages.NoSignerIdError, Enums.Messages.Warning, Utils.getToastrErrorOptions());
            return false;
        }

        if (
            this.$isMarkedForApproval()
            && this.$inputData().attachments.length != this.$configSchema().taskApprove.extraContent.attachments.length
        ) {
            this.toastr.warning(Enums.Messages.AttachmentMandatory, Enums.Messages.Warning, Utils.getToastrErrorOptions());
            return false;
        }

        return true;
    }
}
